import { Card, Stack, Typography } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type React from "react";
import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
	apiClient,
	apiHooks,
	type Schemas,
} from "../../lib/apis/norsk-gassnett-api-hooks";
import Loader from "../../lib/components/atomics/loader";
import LoadingButton from "../../lib/components/atomics/loading-button";
import MUIIcon from "../../lib/components/atomics/mui-icon";
import SubpageWrapper from "../../lib/components/atomics/subpage-wrapper";
import { InfoText } from "../../lib/components/complex/info-text";
import { downloadFile } from "../../lib/helpers/utils";

interface ServiceReportProps {
	report: Schemas["ServiceReport"];
}

const ServiceReport: React.FC<ServiceReportProps> = ({ report }) => {
	const { t } = useTranslation();
	const { mutateAsync, error, isPending } = apiHooks.useMutation(
		"get",
		"/v1/service-report/{id}",
	);

	const handleGetPdf = useCallback(async () => {
		const data = await mutateAsync({
			params: {
				path: {
					id: report.SystemId,
				},
			},
		});
		let pdfName = t("service_reports.standard_report_name");

		if (report.dato) {
			const date = dayjs(report.dato).format("YYYY-MM-DD");
			pdfName = t("service_reports.report_name").replace("{{date}}", date);
		}
		await downloadFile({
			name: pdfName,
			data: data.data.serviceReport.pdfContent,
		});
	}, [report, mutateAsync, t]);

	return (
		<Card sx={{ borderRadius: 0, p: 0, width: "100%" }}>
			<Stack
				justifyContent="space-between"
				alignItems="center"
				direction="row"
				gap={3}
				sx={{
					py: 2,
					px: 3,
				}}
			>
				<Stack>
					<Typography variant="h4" fontSize="1.1rem">
						{t("service_reports.card_title")}
					</Typography>
					<Typography variant="subtitle2" fontSize="1rem">
						{dayjs(report.dato).format("DD.MM.YYYY")}
					</Typography>
				</Stack>
				<Stack direction="row" gap={1}>
					<LoadingButton
						square
						variant="outlined"
						startIcon={
							<MUIIcon name={error ? "file_download_off" : "download"} />
						}
						loading={isPending}
						disabled={!!error}
						title={t("service_reports.download") ?? ""}
						onClick={!error && handleGetPdf}
					>
						{t("service_reports.download")}
					</LoadingButton>
				</Stack>
			</Stack>
		</Card>
	);
};

const ServiceReports: React.FC = () => {
	const { t } = useTranslation();
	const { data, error, fetchNextPage, hasNextPage, isFetching, isPending } =
		useInfiniteQuery({
			queryKey: ["get", "service-report"],
			queryFn: async ({ pageParam }) => {
				const take = 5;

				const res = await apiClient.GET("/v1/service-report", {
					params: { query: { take: take, skip: pageParam } },
				});

				if (!res.data?.data || res.data?.error) {
					throw new Error(res.data?.error?.message ?? t("error.unknown"));
				}

				return { data: res.data.data.serviceReports, skip: pageParam, take };
			},
			initialPageParam: 0,
			getNextPageParam: (lastPage) =>
				(lastPage.data.length ?? 0) < lastPage.take
					? null
					: lastPage.skip + lastPage.take,
		});

	return (
		<SubpageWrapper title={t("service_reports.title")} backRef="/">
			<InfoText translation="service_reports.description" />
			<Stack width="100%" gap={2} sx={{ pt: 3, pb: 7 }}>
				{isPending ? (
					<Loader />
				) : error ? (
					<Typography
						variant="subtitle1"
						textAlign="center"
						sx={{ opacity: 0.4 }}
					>
						{t("error.unknown")}
					</Typography>
				) : data.pages.reduce((acc, el) => acc + el.data.length, 0) < 1 ? (
					<Typography
						variant="subtitle1"
						textAlign="center"
						sx={{ opacity: 0.4 }}
					>
						{t("service_reports.no_reports")}
					</Typography>
				) : (
					data.pages.map((group, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: Its good since its coming from tanstack query
						<Fragment key={i}>
							{group.data.map((report) => (
								<ServiceReport key={report.SystemId} report={report} />
							))}
						</Fragment>
					))
				)}
			</Stack>
			<LoadingButton
				variant="outlined"
				disabled={!hasNextPage || isFetching}
				loading={isFetching}
				endIcon={
					<MUIIcon name={hasNextPage ? "event_repeat" : "event_available"} />
				}
				onClick={() => fetchNextPage()}
			>
				{t(`service_reports.${hasNextPage ? "show_more" : "no_more_reports"}`)}
			</LoadingButton>
		</SubpageWrapper>
	);
};

export default ServiceReports;
